<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  authType: {
    type: String,
    default: 'none',
  },
  templateId: {
    type: String,
    default: '',
  },
  endpointUrl: {
    type: String,
    default: '',
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  // Null for every tool that has no catalog to index, and for a catalog tool that has
  // never been indexed. Both cases simply show nothing.
  indexing: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const reindexableTemplateIds = [
  'woocommerce_price_lookup',
  'google_sheets_apps_script_catalog_lookup',
  'google_sheets_apps_script_wholesale_lookup',
  'google_sheets_apps_script_retail_lookup',
  'google_sheets_lookup',
];

const canReindex = computed(() => {
  if (reindexableTemplateIds.includes(props.templateId)) {
    return true;
  }

  return (
    props.endpointUrl.includes('/wp-json/wc/v3/products') ||
    props.endpointUrl.includes('sheets.googleapis.com/v4/spreadsheets') ||
    props.endpointUrl.includes('source=google_sheets') ||
    props.endpointUrl.includes('price_tier=')
  );
});

const menuItems = computed(() => [
  ...(canReindex.value
    ? [
        {
          label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.REINDEX_TOOL'),
          value: 'reindex',
          action: 'reindex',
          icon: 'i-lucide-refresh-cw',
        },
      ]
    : []),
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.EDIT_TOOL'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.DELETE_TOOL'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const timestamp = computed(() =>
  dynamicTime(props.updatedAt || props.createdAt)
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const authTypeLabel = computed(() => {
  return t(
    `CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.${props.authType.toUpperCase()}`
  );
});

const indexingState = computed(() => props.indexing || {});

const isIndexing = computed(() =>
  ['queued', 'running'].includes(indexingState.value.status)
);

const hasIndexingFailed = computed(
  () => indexingState.value.status === 'failed'
);

const indexingProgress = computed(() => {
  const { processed, total } = indexingState.value;
  if (!total) return null;

  return {
    processed: processed || 0,
    total,
    percent: Math.min(100, Math.round(((processed || 0) / total) * 100)),
  };
});

// Downloading the catalog is one request with nothing to count; embedding it is one call
// per row, and that is the wait worth putting a number on.
const indexingLabel = computed(() => {
  const { status, stage, indexed_count: indexedCount } = indexingState.value;

  if (status === 'queued') {
    return t('CAPTAIN.CUSTOM_TOOLS.INDEXING.QUEUED');
  }

  if (status === 'running') {
    if (stage === 'indexing' && indexingProgress.value) {
      return t(
        'CAPTAIN.CUSTOM_TOOLS.INDEXING.PROGRESS',
        indexingProgress.value
      );
    }
    return t('CAPTAIN.CUSTOM_TOOLS.INDEXING.FETCHING');
  }

  if (status === 'failed') {
    return t('CAPTAIN.CUSTOM_TOOLS.INDEXING.FAILED');
  }

  if (status === 'completed') {
    return t('CAPTAIN.CUSTOM_TOOLS.INDEXING.COMPLETED', {
      count: indexedCount || 0,
      time: dynamicTime(indexingState.value.indexed_at),
    });
  }

  return '';
});

// A run that stored every row but could not embed some of them: those products are still
// found by their words, never by meaning, and nothing else on the card would say so.
const embeddingFailures = computed(() => {
  if (indexingState.value.status !== 'completed') return 0;

  return indexingState.value.embedding_failures || 0;
});

const indexingIcon = computed(() => {
  if (isIndexing.value) return 'i-lucide-loader-2 animate-spin';
  if (hasIndexingFailed.value) return 'i-lucide-triangle-alert';

  return 'i-lucide-database';
});
</script>

<template>
  <CardLayout class="relative">
    <div class="relative flex justify-between w-full min-w-0 gap-1">
      <span class="min-w-0 font-medium truncate text-base text-n-slate-12">
        {{ title }}
      </span>
      <div class="flex items-center gap-2 shrink-0">
        <Policy
          v-on-clickaway="() => toggleDropdown(false)"
          :permissions="['administrator']"
          class="relative flex items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:right-0 top-full"
            @action="handleAction($event)"
          />
        </Policy>
      </div>
    </div>
    <div class="flex items-center justify-between w-full min-w-0 gap-4">
      <div class="flex items-center flex-1 min-w-0 gap-3">
        <span
          v-if="description"
          class="flex-1 min-w-0 text-sm truncate text-n-slate-11"
          :title="description"
        >
          {{ description }}
        </span>
        <span
          v-if="authType !== 'none'"
          class="text-sm shrink-0 text-n-slate-11 inline-flex items-center gap-1"
        >
          <i class="i-lucide-lock text-base" />
          {{ authTypeLabel }}
        </span>
      </div>
      <span class="text-sm text-n-slate-11 line-clamp-1 shrink-0">
        {{ timestamp }}
      </span>
    </div>
    <div v-if="indexingLabel" class="flex flex-col w-full min-w-0 gap-1.5">
      <div
        class="flex items-center min-w-0 gap-2 text-sm"
        :class="hasIndexingFailed ? 'text-n-ruby-11' : 'text-n-slate-11'"
      >
        <span :class="indexingIcon" class="text-base shrink-0" />
        <span class="min-w-0 truncate">{{ indexingLabel }}</span>
      </div>
      <div
        v-if="isIndexing && indexingProgress"
        class="w-full h-1 overflow-hidden rounded-full bg-n-alpha-2"
      >
        <div
          class="h-full rounded-full bg-n-brand transition-[width] duration-500"
          :style="{ width: `${indexingProgress.percent}%` }"
        />
      </div>
      <span
        v-if="hasIndexingFailed && indexingState.error"
        class="text-sm break-words text-n-slate-11"
      >
        {{ indexingState.error }}
      </span>
      <span
        v-if="embeddingFailures"
        class="inline-flex items-center gap-2 text-sm text-n-amber-12"
      >
        <span class="i-lucide-triangle-alert text-base shrink-0" />
        {{
          $t('CAPTAIN.CUSTOM_TOOLS.INDEXING.EMBEDDING_FAILURES', {
            count: embeddingFailures,
          })
        }}
      </span>
    </div>
  </CardLayout>
</template>
